/* eslint-disable react/require-default-props */
import Loader from 'components/atoms/loader';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import {
  FollowupOnlineSearchState,
  FollowupOnlineSearchTypes,
} from 'store/modules/followup_online_search_store/types';
import { ProfileState } from 'store/modules/profile_store/types';
import ReCAPTCHA from 'react-google-recaptcha';
import EmptyResult from 'components/atoms/empty_result';
import PerPageOptions from 'components/atoms/per_page_options';
import TotalEntriesLabel from 'components/atoms/total_entries_label';
import SelectButton from 'components/atoms/select_button';
import ItemTools from 'components/molecules/item_tools';
import ItemShare from 'components/atoms/item_share';
import Pagination from 'components/atoms/pagination';
import InAnalysisAlert from 'components/molecules/in_analysis_alert';
import { toggleSelected } from 'services/toggleSelected';
import { useReactToPrint } from 'react-to-print';
import FollowupItem from 'components/molecules/followup_item';
import { FollowupState } from 'store/modules/followup_store/types';
import { getDownloadFollowups } from 'services/actions/followup_actions';
import OpenTutorial from 'components/atoms/open_tutorial';
import { Container } from './styles';

interface FollowupListProps {
  searchBarOpened: boolean;
}

const FollowupList = (props: FollowupListProps) => {
  const { searchBarOpened } = props;
  const dispatch = useDispatch();
  const { followups, request } = useSelector<ApplicationState, FollowupState>(
    (state) => state.session.followups,
  );

  const { followupOnlineSearch } = useSelector<ApplicationState, FollowupOnlineSearchState>(
    (state) => state.session.followupOnlineSearch,
  );

  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);
  const [showCaptcha, setShowCaptcha] = useState<boolean>(false);
  const [selectedFollowups, setSelectedFollowups] = useState<Array<number>>([]);
  const [token, setToken] = useState<string | null>(null);
  const [showShareWindow, setShowShareWindow] = useState<boolean>(false);
  const [scrollToolsClass, setScrollToolsClass] = useState<string>('');
  const [wholePageSelected, setWholePageSelected] = useState<boolean>(false);

  const recaptchaRef = React.createRef<ReCAPTCHA>();
  const componentRef = useRef() as React.MutableRefObject<HTMLInputElement>;

  const toggleSelectedBiddings = (id: number, updateAll = false) => {
    toggleSelected({
      id,
      selectedList: selectedFollowups,
      setSelectedList: setSelectedFollowups,
      sourceList: followups.followup_list,
      wholePageSelected,
      updateAll,
    });
  };

  const handlePostsPerPage = async (value: number | undefined) => {
    if (!value) return;

    dispatch({ type: FollowupOnlineSearchTypes.UPDATE_POSTS_PER_PAGE, postsPerPage: value });
    dispatch({ type: FollowupOnlineSearchTypes.UPDATE_CURRENT_PAGE, currentPage: 1 });
  };

  useEffect(() => {
    const windowDiv = document.getElementById('windowDiv');
    windowDiv?.addEventListener('scroll', () => {
      setScrollToolsClass(windowDiv?.scrollTop > 10 ? 'border-bottom' : '');
    });
  }, []);

  useEffect(() => {
    if (!followups) return;

    setWholePageSelected(
      followups.followup_list
        ?.map((followup) => followup.id)
        .filter((followup) => selectedFollowups.includes(followup))?.length ===
        followups?.followup_list?.length,
    );
  }, [
    selectedFollowups,
    followupOnlineSearch.currentPage,
    followupOnlineSearch.postsPerPage,
    followups,
  ]);

  const perPageOptions = [
    { value: 20, label: 20 },
    { value: 40, label: 40 },
    { value: 60, label: 60 },
    { value: 80, label: 80 },
    { value: 100, label: 100 },
  ];

  const downloadFile = (fileExtension: string) => {
    getDownloadFollowups(
      fileExtension,
      'acompanhamentos',
      selectedFollowups,
      selectedFollowups?.length,
      false,
    );
  };

  const handleShare = () => {
    setShowShareWindow(!showShareWindow);
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: 'conlicitacao',
  });

  const updateCurrentPage = (currentPage = 1) => {
    dispatch({ type: FollowupOnlineSearchTypes.UPDATE_CURRENT_PAGE, currentPage });
  };

  return (
    <Container>
      <div
        className={`result-container col-12 ${
          searchBarOpened ? 'col-lg-9 offset-lg-3 pr-md-0' : 'col-lg-12 p-md-0'
        }`}
      >
        {profile.enable_captcha && showCaptcha === true && token === null && (
          <div className="w-100 d-flex justify-content-center mt-5">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey="6Lcex2sdAAAAAKMks7alzUBCyRFy88kgqM07DcwW"
              onChange={(recaptchaToken: React.SetStateAction<string | null>) => {
                setShowCaptcha(false);
                setToken(recaptchaToken);
              }}
              hl="pt-BR"
            />
          </div>
        )}
        {profile.enable_captcha && showCaptcha === false && token === null && (
          <div className="d-flex justify-content-center mt-5">
            <button
              type="button"
              className="btn btn-success btn-sm mr-1"
              onClick={() => {
                setShowCaptcha(true);
              }}
              disabled={showCaptcha}
            >
              Ver Acompanhamentos
            </button>
          </div>
        )}
        {request?.loading && (!profile.enable_captcha || token !== null) && (
          <div className="d-flex align-items-center justify-content-center loading-container">
            <Loader />
          </div>
        )}
        {request?.loaded &&
          (!profile.enable_captcha || token !== null) &&
          (!profile.in_analysis ||
            (profile.in_analysis && followupOnlineSearch.currentPage === 1)) &&
          followups?.followup_list?.length > 0 && (
            <div className="row">
              <div className="col-12 mx-auto col-lg-11 p-md-0">
                <div className="d-md-flex d-block align-items-center mt-4">
                  <h4 className="mb-0 font-weight-bold">Encontrar Acompanhamentos</h4>
                  <OpenTutorial type="modal" source="followup" />
                </div>
                <div className={`row list-tools py-1 ${scrollToolsClass}`}>
                  <div className="position-relative col-lg-6 d-block py-3 px-md-2 px-4">
                    <SelectButton
                      isChecked={wholePageSelected}
                      id={0}
                      toggleSelected={toggleSelectedBiddings}
                      text="Selecionar página"
                    />
                    {selectedFollowups?.length > 0 && (
                      <div className="itemToolsSelected ml-3">
                        {selectedFollowups?.length}{' '}
                        {selectedFollowups?.length === 1
                          ? 'registro selecionado.'
                          : 'registros selecionados.'}
                        <button
                          type="button"
                          className="btn btn-link m-0 py-0 px-1 border-0"
                          onClick={() => setSelectedFollowups([])}
                        >
                          Limpar seleção.
                        </button>
                      </div>
                    )}
                  </div>
                  <ItemTools
                    enabledButtons={selectedFollowups?.length > 0}
                    disabledPopoverLabel="o acompanhamento"
                    downloadFile={downloadFile}
                    handleShare={handleShare}
                    handlePrint={handlePrint}
                  />
                  {showShareWindow && (
                    <ItemShare
                      setShowShareWindow={setShowShareWindow}
                      selectedItems={selectedFollowups}
                      source="followup"
                    />
                  )}
                </div>
                {profile.in_analysis && <InAnalysisAlert />}

                <div className="row mb-4">
                  <div className="col-6">
                    <PerPageOptions
                      options={perPageOptions}
                      postsPerPage={followupOnlineSearch.postsPerPage}
                      handleSelect={handlePostsPerPage}
                    />
                  </div>
                  <div className="col-md-6 d-flex align-items-center justify-content-end text-secondary">
                    <TotalEntriesLabel
                      itemLabel="acompanhamentos"
                      totalEntries={followups?.total_entries}
                    />
                  </div>
                </div>

                <div ref={componentRef} className="row mx-1 mt-5">
                  <div className="col-12">
                    {request?.loaded &&
                      followups?.followup_list?.map((followup, index) => (
                        <FollowupItem
                          key={followup.id}
                          followup={followup}
                          followupIndex={
                            index +
                            (followupOnlineSearch.currentPage - 1) *
                              followupOnlineSearch.postsPerPage
                          }
                          isChecked={selectedFollowups.includes(followup.id)}
                          toggleSelected={toggleSelectedBiddings}
                          source="followupOnlineSearch"
                        />
                      ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        {request?.error && (
          <EmptyResult title="Desculpe!" description="Algo deu errado, tente novamente." />
        )}
        {request?.loaded &&
          (!profile.enable_captcha || token !== null) &&
          (!profile.in_analysis ||
            (profile.in_analysis && followupOnlineSearch.currentPage === 1)) &&
          followups?.total_entries === 0 && (
            <EmptyResult
              title="Desculpe!"
              description="Não temos acompanhamentos que correspondam a esses filtros."
            />
          )}
        {request?.loaded && profile.in_analysis && followupOnlineSearch.currentPage !== 1 && (
          <div className="alert alert-warning">
            Estamos finalizando a liberação do seu cadastro, aguarde que em breve todos os avisos de
            acompanhamento estarão disponíveis para você.
          </div>
        )}
        {request?.loaded && followups.total_entries >= 0 && (
          <div className="row d-print-none">
            <div className="col-12 mx-auto mb-5 pb-5 col-lg-11 p-md-0">
              <Pagination
                postsPerPage={followupOnlineSearch.postsPerPage}
                totalPosts={followups?.total_entries}
                paginate={updateCurrentPage}
                currentPage={followupOnlineSearch.currentPage || 1}
                firstAndLastPage
              />
            </div>
          </div>
        )}
      </div>
    </Container>
  );
};

export default FollowupList;
